// ~/composables/useImagePrefetch.ts
import { reactive } from 'vue'
import { useVmStore } from '~/stores/vm'

const inflightRequests: Map<string, Promise<string | null>> = new Map();
export const imageStore = reactive(new Map<string,string>())

const DB_NAME     = 'product-images'
const STORE_NAME  = 'images'
const DB_VERSION  = 1
const MAX_CHUNKS  = 3  // maximum number of JSON files to fetch

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      return reject(new Error('IndexedDB not available'))
    }
    const req = window.indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror   = () => reject(req.error)
  })
}

/**
 * Clears and reloads all image chunks if and only if
 * the “public_files_last_updated” timestamp in
 * /01_update_this_when_touching_anything_in_this_folder.json
 * differs from what’s in IndexedDB.  Otherwise just
 * populates in-memory cache from IndexedDB.
 */
export async function prefetchProductImages() {
  const vm = useVmStore()
  let db: IDBDatabase
  let remoteTimestamp: string|null = null
  let storedTimestamp: string|null = null
  let needFullFetch = true

  try {
    // 1) Fetch the "update file" first
    const updRes = await fetch('/01_update_this_when_touching_anything_in_this_folder.json')
    if (updRes.ok) {
      const payload = await updRes.json() as { public_files_last_updated: string }
      remoteTimestamp = payload.public_files_last_updated
    } else {
      console.warn(`Couldn’t fetch update manifest: ${updRes.status}`)
    }

    // 2) Open IndexedDB and read the stored timestamp
    db = await openDB()
    if (remoteTimestamp != null) {
      const tx0 = db.transaction(STORE_NAME, 'readonly')
      const store0 = tx0.objectStore(STORE_NAME)
      const req0 = store0.get('public_files_last_updated')
      storedTimestamp = await new Promise<string|null>((res, rej) => {
        req0.onsuccess = () => res(req0.result?.imageBase64 || null)
        req0.onerror = () => rej(req0.error)
      })

      // 3) Compare
      if (storedTimestamp === remoteTimestamp) {
        needFullFetch = false
      }
    }
  } catch (e) {
    console.warn('Error checking update timestamp, forcing full fetch:', e)
    needFullFetch = true
  }

  if (!needFullFetch) {
    // === no update: just read everything from IDB into imageStore ===
    try {
      db = await openDB()
      const txAll = db.transaction(STORE_NAME, 'readonly')
      const storeAll = txAll.objectStore(STORE_NAME)
      const allReq = storeAll.getAll()
      const allEntries: Array<{ id: string; imageBase64: string }> = await new Promise((res, rej) => {
        allReq.onsuccess = () => res(allReq.result || [])
        allReq.onerror   = () => rej(allReq.error)
      })

      for (const { id, imageBase64 } of allEntries) {
        if (id === 'public_files_last_updated') continue
        imageStore.set(String(id), imageBase64)
      }
      return
    } catch (e) {
      console.warn('Failed to populate in-memory cache, will re-fetch all chunks:', e)
      // fall through to full-fetch path
    }
  }

  // === full-fetch path: clear store + fetch each chunk + re-populate ===
  try {
    // clear everything
    await new Promise<void>((res, rej) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      tx.objectStore(STORE_NAME).clear()
      tx.oncomplete = () => res()
      tx.onerror    = () => rej(tx.error)
    })

    // fetch each JSON chunk and write into IDB + in-memory
    for (let i = 1; i <= MAX_CHUNKS; i++) {
      const url = `/Base64ImgData.json-${i}`

      vm.imageLoadStart() // bump counter before fetch
      try {
        const r = await fetch(url)
        if (!r.ok) {
          console.warn(`Chunk ${i} not found (status ${r.status}), skipping`)
          continue
        }
        const chunk = await r.json() as Array<{ id: number|string; imageBase64: string }>
        await new Promise<void>((res, rej) => {
          const tx = db.transaction(STORE_NAME, 'readwrite')
          const store = tx.objectStore(STORE_NAME)
          for (const { id, imageBase64 } of chunk) {
            store.put({ id, imageBase64 })
            imageStore.set(String(id), imageBase64)
            //console.log('Set imageStore', String(id))
          }
          tx.oncomplete = () => res()
          tx.onerror    = () => rej(tx.error)
        })
      } finally {
        vm.imageLoadEnd() // decrement counter after parse/write
      }
    }

    // finally, record the new timestamp
    if (remoteTimestamp != null) {
      await new Promise<void>((res, rej) => {
        const tx = db.transaction(STORE_NAME, 'readwrite')
        tx.objectStore(STORE_NAME).put({
          id: 'public_files_last_updated',
          imageBase64: remoteTimestamp
        })
        tx.oncomplete = () => res()
        tx.onerror    = () => rej(tx.error)
      })
      imageStore.set('public_files_last_updated', remoteTimestamp)
    }
  } catch (e) {
    console.warn('prefetchProductImages failed during full-fetch:', e)
  }
}

function withTimeout<T>(promise: Promise<T>, ms = 1000): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`Timeout after ${ms}ms`));
    }, ms);

    promise.then(
      (val) => {
        clearTimeout(timer);
        resolve(val);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

export async function getImage(id: number | string): Promise<string | null> {

  const key = String(id);

  // Return from in-memory cache
  if (imageStore.has(key)) {
    return imageStore.get(key) || null;
  }

  // If request is already in flight, return the same promise
  if (inflightRequests.has(key)) {
    return inflightRequests.get(key)!;
  }

  const fetchPromise = (async () => {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(key);

      const result = await withTimeout(
        new Promise<string | null>((res, rej) => {
          req.onsuccess = () => {
            const image = req.result?.imageBase64 || null;
            if (image) imageStore.set(String(key), image); // Save to cache
            res(image);
          };
          req.onerror = () => rej(req.error);
        }),
        3000 // 3 second timeout
      );

      return result;
    } catch (err) {
      console.warn(`getImage(${id}) failed:`, err);
      return null;
    } finally {
      inflightRequests.delete(key); // Clean up
    }
  })();

  inflightRequests.set(key, fetchPromise);
  return fetchPromise;
}
